'use client';

import type { i18n } from 'i18next';
import type { RegisterDocumentItem } from '@/lib/backend-api';
import { formatCapturedDate } from './companyDetailUtils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Download, Loader2, RefreshCw } from 'lucide-react';
import { WEB_CREDIT_COSTS } from '@/config/credit-costs.config';
import type { TFunction } from 'i18next';

interface RegisterDocEarlierVersionsProps {
  earlierDocuments: RegisterDocumentItem[];
  storedDocumentKeys: Set<string>;
  loadingFileTypes: Set<string>;
  isAutoUpdating: boolean;
  downloadingFileTypes: Set<string>;
  t: TFunction;
  fileErrorDetails: Record<string, { code: string; message?: string }>;
  getFileName: (fileType: string, publicName?: string) => string;
  i18n: i18n;
  formatDate: (dateStr: string | null | undefined) => string | null;
  isAuthenticated: boolean;
  isDeleted: boolean;
  openRegisterFile: (fileType: string, refresh: boolean, documentId?: number, docDate?: string | null) => Promise<void>;
}

export function RegisterDocEarlierVersions({
  earlierDocuments,
  storedDocumentKeys,
  loadingFileTypes,
  isAutoUpdating,
  downloadingFileTypes,
  t,
  fileErrorDetails,
  getFileName,
  i18n,
  formatDate,
  isAuthenticated,
  isDeleted,
  openRegisterFile,
}: RegisterDocEarlierVersionsProps) {
  return (
    <div className="flex flex-col">
      {earlierDocuments.map((item) => {
        const itemKey = `${item.file_type}:${item.id}`;
        const stored =
          item.stored || storedDocumentKeys.has(itemKey);
        const loading =
          loadingFileTypes.has(itemKey) ||
          (!stored && isAutoUpdating);
        const downloading = downloadingFileTypes.has(itemKey);
        const date =
          item.doc_date ?? item.published_at?.slice(0, 10);
        const dateLabel = date
          ? t('companyDetail.registerDocs.versionOf', {
              date: '__DATE__',
            }).split('__DATE__')
          : null;
        const detail = fileErrorDetails[itemKey];
        const translatedError = detail
          ? t(
              `companyDetail.registerDocs.errors.${detail.code}`,
              {
                name: getFileName(item.file_type),
                defaultValue: '',
              },
            )
          : '';
        return (
          <div key={item.id} className="py-1">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="min-w-0 flex-1 text-[10px] lg:text-xs text-muted-foreground">
                {date && dateLabel ? (
                  <span>
                    {dateLabel[0]}
                    <time dateTime={item.doc_date ?? date}>
                      {formatCapturedDate(
                        date,
                        i18n.language,
                      ) ?? formatDate(date)}
                    </time>
                    {dateLabel[1]}
                  </span>
                ) : (
                  getFileName(item.file_type)
                )}
              </div>
              <div className="w-[110px] shrink-0">
                {isAuthenticated &&
                  (stored || !isDeleted) && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="min-h-[44px] sm:min-h-0 -my-2 sm:my-0 h-auto sm:h-7 text-[10px] sm:text-sm lg:text-xs px-2 sm:px-3 border-0 lg:border bg-transparent lg:bg-background rounded-full shadow-none w-full"
                            disabled={loading || downloading}
                            onClick={() =>
                              openRegisterFile(
                                item.file_type,
                                !stored,
                                item.id,
                                date,
                              )
                            }
                          >
                            {loading || downloading ? (
                              <Loader2 className="h-3 w-3 sm:h-3.5 sm:w-3.5 animate-spin mr-1" />
                            ) : stored ? (
                              <Download className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" />
                            ) : (
                              <RefreshCw className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" />
                            )}
                            {t(
                              stored
                                ? 'companyDetail.registerDocs.view'
                                : 'companyDetail.registerDocs.fetch',
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {t(
                            stored
                              ? 'credits.costTooltip'
                              : 'credits.costTooltipRefresh',
                            {
                              n: WEB_CREDIT_COSTS.registerFileDownload,
                            },
                          )}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
              </div>
            </div>
            {detail && (
              <p
                className="text-[10px] text-amber-600 dark:text-amber-400"
                role="alert"
              >
                {translatedError ||
                  detail.message ||
                  t(
                    'companyDetail.registerDocs.errors.generic',
                    { name: getFileName(item.file_type) },
                  )}
              </p>
            )}
          </div>
        );
      })}
      {!isAuthenticated && (
        <p className="text-[10px] text-muted-foreground">
          {t('companyDetail.registerDocs.signup')}
        </p>
      )}
    </div>
  );
}
